import type { SVGProps } from "react";

/**
 * Hand-drawn product illustrations for the catalog — flat geometric line art,
 * one consistent stroke weight + a single tinted fill block per shape, so a
 * piece reads the same whether it's a 24px list icon or a 220px hero prop.
 */

export type ProductArtTone = "tag" | "verify" | "ink";

export interface ProductArtProps extends SVGProps<SVGSVGElement> {
  tone?: ProductArtTone;
}

const toneVar: Record<ProductArtTone, string> = {
  tag: "var(--color-primary)",
  verify: "var(--color-accent)",
  ink: "var(--color-foreground)",
};

function Frame({
  tone = "tag",
  children,
  ...props
}: ProductArtProps & { children: React.ReactNode }) {
  const fill = toneVar[tone];
  return (
    <svg
      viewBox="0 0 120 120"
      fill="none"
      stroke="currentColor"
      strokeWidth={5}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ ["--art-fill" as string]: fill }}
      {...props}
    >
      {children}
    </svg>
  );
}

export function SmartphoneArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="26" y="8" width="68" height="104" rx="14" />
      <rect
        x="36"
        y="20"
        width="48"
        height="72"
        rx="4"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
      <circle cx="60" cy="102" r="3" fill="currentColor" stroke="none" />
      <circle cx="60" cy="16" r="1.6" fill="currentColor" stroke="none" />
    </Frame>
  );
}

export function EscooterArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <line x1="12" y1="92" x2="72" y2="92" strokeWidth="7" />
      <circle cx="16" cy="103" r="10" />
      <circle cx="80" cy="103" r="10" />
      <path d="M68 92 L94 24" />
      <line x1="80" y1="24" x2="108" y2="24" />
      <circle
        cx="94"
        cy="30"
        r="3.5"
        fill="var(--art-fill)"
        fillOpacity="0.5"
      />
    </Frame>
  );
}

export function LaptopArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="24" y="14" width="72" height="62" rx="6" />
      <rect
        x="32"
        y="22"
        width="56"
        height="46"
        rx="3"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
      <path d="M14 86 h92 l-8 14 H22 Z" />
      <circle cx="60" cy="18.5" r="1.4" fill="currentColor" stroke="none" />
    </Frame>
  );
}

export function HeadphonesArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <path d="M25 58 A35 35 0 0 1 95 58" />
      <rect
        x="10"
        y="52"
        width="21"
        height="34"
        rx="10"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
      <rect
        x="89"
        y="52"
        width="21"
        height="34"
        rx="10"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
    </Frame>
  );
}

export function SmartwatchArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <path d="M44 34 L48 12 H72 L76 34" />
      <path d="M44 86 L48 108 H72 L76 86" />
      <rect x="35" y="34" width="50" height="52" rx="14" />
      <rect
        x="43"
        y="42"
        width="34"
        height="36"
        rx="8"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
      <rect
        x="85"
        y="52"
        width="7"
        height="12"
        rx="2"
        fill="currentColor"
        stroke="none"
      />
    </Frame>
  );
}

export function TabletArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="30" y="10" width="60" height="100" rx="10" />
      <rect
        x="38"
        y="22"
        width="44"
        height="76"
        rx="4"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
      <circle cx="60" cy="16" r="1.6" fill="currentColor" stroke="none" />
    </Frame>
  );
}

export function TvArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="10" y="18" width="100" height="64" rx="6" />
      <rect
        x="19"
        y="27"
        width="82"
        height="46"
        rx="3"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
      <line x1="60" y1="82" x2="60" y2="94" strokeWidth="6" />
      <line x1="34" y1="104" x2="86" y2="104" strokeWidth="6" />
    </Frame>
  );
}

export function CameraArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="12" y="38" width="96" height="54" rx="10" />
      <rect x="40" y="24" width="26" height="16" rx="4" />
      <circle cx="60" cy="65" r="21" />
      <circle
        cx="60"
        cy="65"
        r="12"
        fill="var(--art-fill)"
        fillOpacity="0.22"
      />
      <rect
        x="86"
        y="46"
        width="12"
        height="9"
        rx="2"
        fill="currentColor"
        stroke="none"
      />
    </Frame>
  );
}

export function GamingPcArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="33" y="8" width="54" height="104" rx="9" />
      <rect
        x="42"
        y="18"
        width="9"
        height="84"
        rx="4"
        fill="var(--art-fill)"
        fillOpacity="0.28"
      />
      <circle cx="72" cy="24" r="3" fill="currentColor" stroke="none" />
      <line x1="63" y1="42" x2="77" y2="42" />
      <line x1="63" y1="52" x2="77" y2="52" />
    </Frame>
  );
}

export function MonitorArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="8" y="20" width="104" height="58" rx="20" />
      <rect
        x="18"
        y="28"
        width="84"
        height="42"
        rx="14"
        fill="var(--art-fill)"
        fillOpacity="0.18"
      />
      <line x1="60" y1="80" x2="60" y2="94" strokeWidth="6" />
      <ellipse cx="60" cy="98" rx="20" ry="5" />
    </Frame>
  );
}

export function MailArt(props: ProductArtProps) {
  return (
    <Frame {...props}>
      <rect x="10" y="26" width="100" height="68" rx="8" />
      <path
        d="M14 32 L60 68 L106 32"
        fill="var(--art-fill)"
        fillOpacity="0.14"
      />
      <rect
        x="76"
        y="10"
        width="30"
        height="20"
        rx="3"
        transform="rotate(8 91 20)"
        fill="var(--art-fill)"
        fillOpacity="0.35"
      />
    </Frame>
  );
}

export const productArt = {
  smartphone: SmartphoneArt,
  escooter: EscooterArt,
  laptop: LaptopArt,
  headphones: HeadphonesArt,
  smartwatch: SmartwatchArt,
  tablet: TabletArt,
  tv: TvArt,
  camera: CameraArt,
  "gaming-pc": GamingPcArt,
  monitor: MonitorArt,
  mail: MailArt,
} as const;

export type ProductArtKey = keyof typeof productArt;
